import React, { ReactElement } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  Button,
  cn,
  Dialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@mindstonehq/ui";
import { Link, useLocation, useNavigate } from "react-router";
import { useIsAuthenticated, useProfile, useCurrentSpace, useUser } from "~/hooks/context-hook";
import { CogIcon, UserCircleIcon, UserIcon } from "@heroicons/react/24/outline";
import { HelpCircleIcon, LogOutIcon } from "lucide-react";
import { useCustomerSupport } from "~/hooks/customer-support";
import Notifications from "~/components/notifications/notifications";
import { PointsAndLevelDisplay } from "~/components/points/points-display";
import metrics from "~/utils/metrics";
import { GiftIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { DiscountCodeDialog } from "~/components/discount-code-dialog";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { displayNameOf } from "~/utils/display-name";

export const PageContainer = ({
  children,
  title,
  noMargin,
  noGap,
  className,
  hideTopBar,
}: React.PropsWithChildren<{
  title?: string;
  noGap?: boolean;
  noMargin?: boolean;
  className?: string;
  hideTopBar?: boolean;
}>) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const user = useProfile();
  const location = useLocation();
  const authenticated = useIsAuthenticated();
  const space = useCurrentSpace();
  const support = useCustomerSupport();
  const breadcrumbs = React.Children.toArray(children)
    .filter(
      (child): child is React.ReactElement<any> =>
        React.isValidElement(child) && child.type === PageContainer.Breadcrumb,
    )
    .reduce((prev, next, i) => {
      return [...prev, next, <BreadcrumbSeparator key={"separator" + i} />];
    }, [] as ReactElement[])
    .slice(0, -1);

  const mainContent = React.Children.toArray(children).filter(
    (child) =>
      !React.isValidElement(child) ||
      (child.type != PageContainer.ButtonAction &&
        child.type != PageContainer.Action &&
        child.type != PageContainer.DialogAction &&
        child.type != PageContainer.Breadcrumb),
  );
  const [container, setContainer] = React.useState<any>(null);
  const currentUser = useUser();
  const referralCode = currentUser?.referrals?.[0]?.referralCode;
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  return (
    <div className={"flex flex-col grow h-full overflow-auto"}>
      {!hideTopBar && (
        <div className="shrink-0 md:sticky top-0 border-b border-border flex bg-card w-full px-4 md:px-6 lg:px-8 xl:px-10">
          <div className="w-full h-16  mx-auto py-4 flex items-center sm:justify-between">
            {!!breadcrumbs.length && !title && (
              <Breadcrumb>
                <BreadcrumbList>{breadcrumbs}</BreadcrumbList>
              </Breadcrumb>
            )}

            {!breadcrumbs.length && title && (
              <h4 className="min-w-0 flex-1 text-xl font-semibold leading-7 line-clamp-1">{title}</h4>
            )}
            {!!breadcrumbs.length && title && (
              <span className="text-destructive"><Trans>Designers said it's not allowed...</Trans></span>
            )}

            <div className="ml-auto hidden h-16 w-full items-center justify-end gap-8 sm:w-fit md:flex">
              {authenticated && (
                <div className="flex flex-row gap-3 items-center">
                  {(space?.programs?.enrolled?.total ?? 0) > 0 && <PointsAndLevelDisplay variant="desktop" />}
                  <Notifications container={container} />
                  <DropdownMenu open={desktopDropdownOpen} onOpenChange={setDesktopDropdownOpen}>
                    <DropdownMenuTrigger asChild className="cursor-pointer">
                      <Button variant="link" className="h-8 w-8 cursor-pointer p-0">
                        <span className="sr-only"><Trans>Your profile</Trans></span>
                        <img
                          className="h-8 w-8 bg-gray-50 rounded-full cursor-pointer"
                          src={user?.profilePictureURL || ""}
                          alt=""
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56">
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex items-center space-x-2">
                          {user?.profilePictureURL ? (
                            <img
                              className="h-8 w-8 bg-gray-50 rounded-full"
                              src={user?.profilePictureURL}
                              alt={t(msg`${displayNameOf(user)}'s profile picture`)}
                            />
                          ) : (
                            <div className="h-8 w-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                              {user?.name
                                ? user.name.charAt(0).toUpperCase()
                                : user?.username?.charAt(0).toUpperCase() || "U"}
                            </div>
                          )}
                          <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">{user?.name || user?.username}</p>
                            <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                          </div>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        <DropdownMenuItem onClick={() => navigate(`/profile/${user?.username}`)}>
                          <UserCircleIcon className="mr-2 h-4 w-4" />
                          <span><Trans>Profile</Trans></span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate("/settings")}>
                          <CogIcon className="mr-2 h-4 w-4" />
                          <span><Trans>Settings</Trans></span>
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        {support.available && (
                          <DropdownMenuItem onSelect={support.open}>
                            <HelpCircleIcon className="mr-2 h-4 w-4" />
                            <span><Trans>Help</Trans></span>
                          </DropdownMenuItem>
                        )}
                        {referralCode && (
                          <DropdownMenuItem
                            onClick={() => {
                              setDesktopDropdownOpen(false);
                              setDiscountDialogOpen(true);
                            }}
                          >
                            <GiftIcon className="mr-2 h-4 w-4" />
                            <span><Trans>Discount code</Trans></span>
                            <span className="ml-auto text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                              <Trans>20% off!</Trans>
                            </span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          metrics.track("logout");
                          navigate("/logout");
                        }}
                      >
                        <LogOutIcon className="mr-2 h-4 w-4" />
                        <span><Trans>Logout</Trans></span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
              {!authenticated && (
                <Button variant={"secondary"} size="sm" asChild>
                  <Link to={`/login?redirectUrl=${location.pathname}`}> <Trans>Login</Trans></Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className=" grow flex flex-col md:overflow-hidden" ref={setContainer}>
        {/*<div className="min-h-full">*/}
        {/* Don't ask why, but this fix scrolling.*/}
        <div
          className={cn(
            " flex-1 grow  md:overflow-auto min-h-0 h-full grid grid-cols-12",
            !!noGap ? "" : "gap-4 sm:gap-5 lg:gap-6 2xl:gap-8",
            !!noMargin ? "" : "p-4 sm:p-5 md:p-6 lg:p-8 xl:p-10 2xl:p-12 ",
            className,
          )}
        >
          {mainContent}
        </div>
        {/*</div>*/}
      </div>
      <DiscountCodeDialog
        open={discountDialogOpen}
        onOpenChange={setDiscountDialogOpen}
        referralCode={referralCode || ""}
      />
    </div>
  );
};

PageContainer.Action = React.forwardRef<HTMLElement, React.ComponentProps<typeof Button>>(({ children }, ref) => {
  return children;
});
PageContainer.ButtonAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ children, ...props }, ref) => {
    return <Button {...props}>{children}</Button>;
  },
);

PageContainer.DialogAction = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Dialog>>(
  ({ children, ...props }, ref) => {
    return <Dialog {...props}>{children}</Dialog>;
  },
);

PageContainer.Breadcrumb = ({ title, href }: { title: string; href?: string }) => {
  if (href) {
    return (
      <BreadcrumbItem>
        <BreadcrumbLink asChild>
          <Link to={href} className="text-placeholder-foreground hover:text-foreground">
            {title}
          </Link>
        </BreadcrumbLink>
      </BreadcrumbItem>
    );
  }
  return (
    <BreadcrumbItem>
      <BreadcrumbLink className="text-foreground font-semibold">{title}</BreadcrumbLink>
    </BreadcrumbItem>
  );
};
export default PageContainer;
